import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import {
  bundles,
  getCountriesForLocale,
  SUPPORTED_COUNTRY_CODES,
} from "@/lib/signupI18n";
import { ContactAccountFields, getContactCopy } from "@/components/ContactAccountFields";

function pickBundle(lang) {
  return bundles[lang] || bundles.en;
}

export default function ProfileSetup() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const lang = user?.preferred_language || "en";
  const t = pickBundle(lang);
  const contactT = getContactCopy(lang);

  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [telephone, setTelephone] = useState("");
  const [country, setCountry] = useState(
    SUPPORTED_COUNTRY_CODES.includes((user?.country || "").toUpperCase())
      ? (user?.country || "").toUpperCase()
      : "",
  );
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const countryOptions = useMemo(() => getCountriesForLocale(lang), [lang]);
  const fieldsValid = [firstName, lastName, streetAddress, city, region, postalCode, telephone].every((v) => v.trim().length > 0)
    && SUPPORTED_COUNTRY_CODES.includes(country)
    && ageConfirmed;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!fieldsValid) {
      setErr(contactT.ageConfirm);
      return;
    }
    setBusy(true);
    try {
      await api.post("/account/complete-profile", {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        street_address: streetAddress.trim(),
        city: city.trim(),
        region: region.trim(),
        postal_code: postalCode.trim(),
        country,
        telephone: telephone.trim(),
        age_18_or_older: true,
      });
      await refresh();
      toast.success(t.profileSaved);
      navigate("/", { replace: true });
    } catch (ex) {
      setErr(formatApiErrorDetail(ex, lang));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-[color:var(--bg-base)]">
      <form onSubmit={submit} className="w-full max-w-lg rounded-3xl bg-white shadow-xl border border-[color:var(--border-stone)] p-6 sm:p-8 space-y-3">
        <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brand)] mb-1">My English Coach Interview Simulator</div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="profile-setup-title">{t.profileTitle}</h1>
        <p className="text-sm text-[color:var(--text-secondary)]">{contactT.contactPurpose}</p>

        <label className="block">
          <span className="block text-xs font-medium text-[color:var(--text-secondary)] mb-1">{t.emailLabel}</span>
          <input type="email" value={user?.email || ""} readOnly className="w-full rounded-lg border border-[color:var(--border-stone)] bg-[color:var(--bg-surface-hover)] px-3 py-2 text-sm text-[color:var(--text-secondary)]" data-testid="profile-email" />
        </label>

        <label className="block">
          <span className="block text-xs font-medium text-[color:var(--text-secondary)] mb-1">{t.firstNameLabel}</span>
          <input type="text" required autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm" data-testid="profile-first-name" placeholder={t.firstNamePlaceholder} />
        </label>

        <ContactAccountFields
          locale={lang}
          lastName={lastName} setLastName={setLastName}
          streetAddress={streetAddress} setStreetAddress={setStreetAddress}
          city={city} setCity={setCity}
          region={region} setRegion={setRegion}
          postalCode={postalCode} setPostalCode={setPostalCode}
          telephone={telephone} setTelephone={setTelephone}
        />

        <label className="block">
          <span className="block text-xs font-medium text-[color:var(--text-secondary)] mb-1">{t.countryLabel}</span>
          <select required value={country} onChange={(e) => setCountry(e.target.value)} className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm bg-white" data-testid="profile-country" autoComplete="country">
            <option value="" disabled>{t.countryPlaceholder}</option>
            {countryOptions.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </label>

        <label className={`flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer ${ageConfirmed ? "border-[color:var(--brand)] bg-[color:var(--brand)]/5" : "border-[color:var(--border-stone)]"}`}>
          <input type="checkbox" checked={ageConfirmed} onChange={(e) => setAgeConfirmed(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[color:var(--brand)]" data-testid="profile-age-confirm" />
          <span className="text-xs leading-snug">{contactT.ageConfirm}</span>
        </label>

        {err && <div className="rounded-lg bg-[color:var(--score-bad)]/10 border border-[color:var(--score-bad)]/30 p-2 text-xs text-[color:var(--score-bad)]" data-testid="profile-error">{err}</div>}

        <button type="submit" disabled={!fieldsValid || busy} className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-5 py-2.5 text-sm font-medium disabled:opacity-40" data-testid="profile-submit">
          {busy ? t.profileSubmitBusy : t.profileSubmit}
        </button>
        <button type="button" onClick={logout} className="w-full text-xs text-[color:var(--text-secondary)] underline" data-testid="profile-logout">{t.profileLogout}</button>
      </form>
    </div>
  );
}
